import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { HiOutlineDocumentText, HiOutlineOfficeBuilding } from "react-icons/hi";
import {
  FiCalendar,
  FiTag,
  FiPhone,
  FiMail,
  FiFileText,
  FiActivity,
  FiClipboard,
  FiExternalLink,
} from "react-icons/fi";
import config from "../../../config";

/* ---------------- NORMALIZER ---------------- */

const normalizeBooking = (booking = {}) => {
  const activity = booking.activity || {};

  // Extract grooming inclusions safely
  const serviceConfig = activity?.config?.find((c) => c.id === "service_inclusions");

  const inclusions = serviceConfig
    ? Object.keys(serviceConfig)
        .filter((k) => !isNaN(k))
        .map((k) => serviceConfig[k])
    : [];

  return {
    id: booking._id,
    date: activity?.date,
    bookingType: booking.booking_type,
    vendor: booking.vendorDetails || {},
    pet: booking.petDetails || {},
    activity: {
      ...activity,
      inclusions,
      groomingSelections: activity?.groomingSelections || {},
    },
  };
};

const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;

  if (
    typeof dateStr === "string" &&
    dateStr.includes("T") &&
    dateStr.endsWith("Z")
  ) {
    return new Date(dateStr);
  }

  if (dateStr.includes("/") && dateStr.includes(",")) {
    try {
      const [datePart, timePart] = dateStr.split(", ");
      const [day, month, year] = datePart.split("/");
      const [time, period] = timePart.split(" ");
      let [hours, minutes] = time.split(":");

      hours = parseInt(hours);
      if (period?.toLowerCase() === "pm" && hours !== 12) hours += 12;
      if (period?.toLowerCase() === "am" && hours === 12) hours = 0;

      return new Date(year, month - 1, day, hours, parseInt(minutes));
    } catch (error) {
      console.error("Date parsing error:", error);
      return null;
    }
  }

  if (dateStr.includes("/")) {
    const [day, month, year] = dateStr.split("/");
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
};

/* ---------------- GROOMING DETAILS ---------------- */

const GroomingDetails = ({ activity }) => {
  const inclusions = activity.inclusions || [];
  const groomingSelections = activity.groomingSelections || {};

  if (!inclusions.length) {
    return (
      <p className="text-sm text-gray-500 italic text-center py-4">
        No grooming services recorded.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <h4 className="font-bold text-lg text-blue-950 flex items-center gap-2">
        <FiTag className="text-blue-600" />
        Grooming Services
      </h4>

      {inclusions.map((group) => (
        <div key={group.inclusion_id} className="bg-white rounded-xl p-4 border">
          <h5 className="font-semibold mb-3">{group.inclusion_headings}</h5>

          {group.selected_options.map((option) => {
            const optionData = groomingSelections?.[group.inclusion_id]?.[option];

            return (
              <div key={option} className="border rounded-lg p-3 mb-3">
                <p className="font-medium">{option}</p>

                {optionData?.note && (
                  <p className="text-sm italic mt-1">{optionData.note}</p>
                )}

                {optionData?.photos?.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {optionData.photos.map((img, i) => (
                      <img
                        key={i}
                        src={`${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${img}`}
                        alt="grooming"
                        className="w-20 h-20 rounded object-cover"
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

/* ---------------- VET CLINIC DETAILS ---------------- */

const VetClinicDetails = ({ activity }) => {
  if (!activity) {
    return (
      <p className="text-sm text-gray-500 italic text-center py-4">
        No veterinary visit details available.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* HEADER */}
      <h4 className="font-bold text-lg text-blue-950 flex items-center gap-1">
        🏥 Vet Clinic Visit
      </h4>

      {/* BASIC INFO */}
      <div className="grid sm:grid-cols-2 gap-1 bg-white p-1">
        <div>
          <p className="text-sm text-gray-500">Veterinarian</p>
          <p className="font-semibold">{activity.veterinarian || "—"}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Clinic</p>
          <p className="font-semibold">{activity.clinic || "—"}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Appetite</p>
          <p className="capitalize font-semibold">{activity.appetite || "—"}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Follow Up</p>
          <p className="font-semibold">{activity.followUp || "—"}</p>
        </div>
      </div>

      {/* VITALS */}
      {activity.vitals && (
        <div className="bg-blue-50 p-6 rounded-2xl">
          <h5 className="font-semibold mb-4 flex items-center gap-2">
            <FiActivity /> Vitals
          </h5>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              Weight:{" "}
              <span className="font-semibold">{activity.vitals.weight || "—"} kg</span>
            </div>
            <div>
              Temperature:{" "}
              <span className="font-semibold">{activity.vitals.temperature || "—"} °C</span>
            </div>
          </div>
        </div>
      )}

      {/* TESTS */}
      {activity.tests?.length > 0 && (
        <div className="bg-white">
          <h5 className="font-semibold mb-4 flex items-center gap-2">
            <FiClipboard /> Diagnostic Tests
          </h5>

          <div className="space-y-1">
            {activity.tests.map((test) => (
              <div
                key={test.id}
                className=" p-4 flex justify-between items-center"
              >
                <div>
                  <p className="font-medium">
                    {test.testCode} – {test.type}
                  </p>
                  <p className="text-sm text-gray-500">
                    {test.category} • {test.sampleType}
                  </p>
                </div>
                <div className="font-semibold">₹{test.price}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MEDICATIONS */}
      {activity.medications?.length > 0 && (
        <div className="bg-white p-4">
          <h5 className="font-semibold mb-4 flex items-center gap-2">
            💊 Medications
          </h5>

          <div className="space-y-2">
            {activity.medications.map((med) => (
              <div key={med.id} className=" p-2">
                <p className="font-medium">{med.name}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {med.dose}
                  {med.unit} • {med.frequency} • {med.duration} days
                </p>
                {med.notes && <p className="text-sm italic mt-1">{med.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NOTES */}
      {activity.notes && (
        <div className="bg-yellow-50 p-2">
          <h5 className="font-semibold mb-2 flex items-center gap-1">
            <FiFileText /> Doctor Notes
          </h5>
          <p>{activity.notes}</p>
        </div>
      )}

      {/* PRESCRIPTION */}
      {activity.prescriptionFile && (
        <div className="text-right">
          <a
            href={activity.prescriptionFile}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
          >
            View Prescription
            <FiExternalLink />
          </a>
        </div>
      )}
    </div>
  );
};

/* ---------------- BOARDING DETAILS ---------------- */
const formatLabel = (text) => {
  if (!text) return "—";
  return text
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const sectionIcons = {
  medication: "💊",
  walking: "🚶",
  feeding: "🍽️",
  sendPicsToClient: "📸",
};

const BoardingDetails = ({ activity }) => {
  const config = activity?.config || [];

  const enabledSections = config.filter((section) => section.enabled);

  if (!enabledSections.length) {
    return (
      <p className="text-sm text-gray-500 italic text-center py-4">
        No boarding instructions provided.
      </p>
    );
  }

  return (
  <div className="space-y-3 text-sm">
    <h4 className="font-semibold text-blue-950 flex items-center gap-2">
      🏠 Boarding Instructions
    </h4>

    {enabledSections.map((section) => (
      <div
        key={section.id}
        className="flex items-start gap-3 border rounded-lg px-3 py-2 bg-gray-50"
      >
        {/* Icon */}
        <div className="text-base">
          {sectionIcons[section.id] || "•"}
        </div>

        {/* Content */}
        <div className="flex-1 space-y-1">
          <p className="font-medium text-gray-800">
            {formatLabel(section.id)}
          </p>

          {/* Medication Type */}
          {Array.isArray(section.items) &&
            section.items.map((item) => (
              <div key={item.id} className="text-gray-600 text-xs">
                {item.name && <span className="font-medium">{item.name}</span>}

                {item.dose && ` • ${item.dose}`}
                {item.doseTiming && ` • ${formatLabel(item.doseTiming)}`}
                {item.time && ` • ${formatLabel(item.time)}`}

                {item.instructions && (
                  <div className="italic text-gray-500 mt-1">
                    {item.instructions}
                  </div>
                )}
              </div>
            ))}

          {/* Simple Sections */}
          {!section.items && (
            <div className="text-gray-600 text-xs">
              {section.count && <span>{section.count} times</span>}

              {section.instructions && (
                <span className="italic ml-2">
                  • {section.instructions}
                </span>
              )}

              {typeof section.value === "boolean" && (
                <span
                  className={`ml-2 font-medium ${
                    section.value
                      ? "text-green-600"
                      : "text-gray-500"
                  }`}
                >
                  {section.value ? "Enabled" : "Disabled"}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);
};
/* ---------------- MAIN COMPONENT ---------------- */

export default function MedicalHistory({ pet }) {
  const [petBookings, setPetBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
  console.log("PET ID:", pet?._id);
}, [pet]);

  useEffect(() => {
    if (!pet?._id) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const token =
          localStorage.getItem("token") || localStorage.getItem("customerToken");
        console.log("TOKEN:", token);
        const res = await axios.get(
          `${config.API_BASE_URL}/customer/getBookingActivies/${pet._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setPetBookings(res.data?.data || []);

        console.log("API RESPONSE:", res.data);
      } catch (err) {
        console.error(err);
        setPetBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pet?._id]);

  /* ---------- NORMALIZE ---------- */
  const normalizedBookings = useMemo(() => {
    return petBookings.map((b) => normalizeBooking(b));
  }, [petBookings]);

  
  petBookings.forEach(b => {
  console.log("REF:", b.booking_ref_id);
});

  /* ---------- LOADING STATE ---------- */
  if (loading) {
    return (
      <div className="flex flex-col items-center py-14">
        <div className="animate-spin h-10 w-10 border-t-4 border-blue-600 rounded-full" />
        <p className="mt-3 text-sm">Loading service history…</p>
      </div>
    );
  }

  if (!normalizedBookings.length) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-2xl">
        <HiOutlineDocumentText className="mx-auto text-5xl text-gray-300 mb-3" />
        <p className="text-sm">No service records found</p>
      </div>
    );
    
  }
console.log("BOOKINGS LENGTH:", normalizedBookings.length);
  /* ---------- RENDER BOOKINGS ---------- */
  return (
    <div className="space-y-4">
      {normalizedBookings.map(({ id, vendor, pet, activity, date, bookingType }) => {
        const parsedDate = parseDDMMYYYY(date);
        const formattedDate = parsedDate
          ? parsedDate.toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "Unknown Date";

        // Detect service type
        const type = bookingType?.toLowerCase();

          const isVet = type === "vet clinic";
          const isGrooming = type === "grooming";
          const isBoarding = type === "boarding";
          const serviceLabel =
          isVet ? "🏥 Vet Clinic" :
          isGrooming ? "🐾 Grooming" :
          "🏠 Boarding";
      // const isVet = activity?.tests?.length || activity?.medications?.length || activity?.vitals;
      //const isGrooming = activity?.inclusions?.length || activity?.groomingSelections;
      //const serviceLabel = isVet ? "🏥 Vet Clinic" : isGrooming ? "🐾 Grooming" : "🏠 Boarding";

        return (
          <div
            key={id}
            className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
          >
            {/* HEADER */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-4 py-2 flex items-center justify-between">
              <div>
                <p className="flex items-center gap-1 text-xs opacity-90">
                  <FiCalendar className="w-3 h-3" /> Booking
                </p>
                <p className="font-semibold text-sm">{formattedDate}</p>
              </div>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-white/20">
                {serviceLabel}
              </span>
            </div>

            {/* BODY */}
            <div className="p-3 space-y-3">
              {/* VENDOR */}
              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white text-sm">
                  <HiOutlineOfficeBuilding className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">
                    {vendor.companyName || vendor.name || "—"}
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-600 mt-1">
                    {vendor.phone && (
                      <span className="flex items-center gap-1">
                        <FiPhone className="w-3 h-3" /> {vendor.phone}
                      </span>
                    )}
                    {vendor.email && (
                      <span className="flex items-center gap-1">
                        <FiMail className="w-3 h-3" /> {vendor.email}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* PET */}
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <div className="grid sm:grid-cols-4 gap-2">
                  <div>
                    <b>Name:</b> {pet.name || "—"}
                  </div>
                  <div>
                    <b>Age:</b> {pet.age || "—"}
                  </div>
                  <div className="capitalize">
                    <b>Gender:</b> {pet.gender || "—"}
                  </div>
                  <div>
                    <b>Weight:</b> {pet.weight_kg || "—"} kg
                  </div>
                </div>
              </div>

              {/* SERVICE DETAILS */}
              <div className="bg-gray-50 p-3 rounded-lg">
                {isVet && <VetClinicDetails activity={activity} />}
                {isGrooming && <GroomingDetails activity={activity} />}
                {isBoarding && <BoardingDetails activity={activity} />}
                {/*  {isVet ? (
                  <VetClinicDetails activity={activity} />
                ) : (
                  <GroomingDetails activity={activity} />
                )}*/}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
